import { useMemo } from "react";
import { motion } from "framer-motion";

export const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, y = 32, className = "", once = true }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, margin: "-70px" }}
    transition={{ duration: 0.9, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const Eyebrow = ({ children, className = "" }) => (
  <p className={`text-xs font-mono uppercase tracking-[0.3em] text-amber-500/90 font-medium ${className}`}>
    {children}
  </p>
);

export const SectionHead = ({ eyebrow, title, copy, align = "left" }) => (
  <div className={`max-w-2xl ${align === "center" ? "mx-auto text-center" : ""}`}>
    <Reveal>
      <Eyebrow className={align === "center" ? "justify-center" : ""}>{eyebrow}</Eyebrow>
    </Reveal>
    <Reveal delay={0.08}>
      <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-stone-50">
        {title}
      </h2>
    </Reveal>
    {copy && (
      <Reveal delay={0.16}>
        <p className="mt-5 text-base sm:text-lg text-stone-400 leading-relaxed font-light">{copy}</p>
      </Reveal>
    )}
  </div>
);

export const EmberField = ({ count = 20, className = "" }) => {
  const particles = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        size: 2 + Math.random() * 4,
        delay: Math.random() * 14,
        dur: 10 + Math.random() * 12,
        o: 0.25 + Math.random() * 0.55,
      })),
    [count]
  );
  return (
    <div aria-hidden className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}>
      {particles.map((p) => (
        <span
          key={p.id}
          className="ember-particle"
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.size,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.dur}s`,
            "--o": p.o,
          }}
        />
      ))}
    </div>
  );
};

export const GoldButton = ({ children, className = "", ...props }) => (
  <button
    {...props}
    className={`group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-amber-600 to-amber-700 px-7 py-3.5 text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-stone-950 shadow-ember-sm transition-[box-shadow,transform] duration-300 hover:shadow-ember hover:-translate-y-0.5 active:translate-y-0 ${className}`}
  >
    <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
    <span className="relative z-10 inline-flex items-center gap-2">{children}</span>
  </button>
);

export const GhostButton = ({ children, className = "", ...props }) => (
  <button
    {...props}
    className={`inline-flex items-center justify-center gap-2 rounded-full border border-amber-500/30 bg-stone-950/40 px-7 py-3.5 text-xs sm:text-sm font-medium uppercase tracking-[0.18em] text-stone-100 backdrop-blur-sm transition-[border-color,background-color,transform] duration-300 hover:border-amber-400/60 hover:bg-amber-950/30 hover:-translate-y-0.5 ${className}`}
  >
    {children}
  </button>
);
